import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const PARISHES = [
  "Westmoreland", "Hanover", "St. Elizabeth", "St. James", "Trelawny",
  "Manchester", "Clarendon", "St. Ann", "St. Catherine", "St. Mary", "Portland", "St. Thomas", "St. Andrew", "Kingston",
];

export const ADMIN_APPS_KEY = ["admin", "top-shop", "applications"];
export const ADMIN_LISTS_KEY = ["admin", "top-shop", "lists"];

export interface ListRow { id: string; name: string; sort_order: number | null; is_active: boolean | null; parish?: string | null; is_catch_all?: boolean }

export const NOT_ASSIGNED = "__unassigned";

/** Active regular communities in sort order, then active catch-all rows last. */
export function orderedActiveCommunities(rows: ListRow[]) {
  const active = rows.filter((r) => r.is_active);
  return [...active.filter((r) => !r.is_catch_all), ...active.filter((r) => r.is_catch_all)];
}

export function communityLabel(r: ListRow) {
  return r.parish && !r.is_catch_all ? `${r.name}, ${r.parish}` : r.name;
}

export function useTopShopProgram() {
  return useQuery({
    queryKey: ["admin", "top-shop", "program"],
    queryFn: async () => {
      const { data, error } = await supabase.from("programs").select("*").eq("slug", "top-shop").single();
      if (error) throw error;
      return data;
    },
  });
}

export function useTopShopLists(programId?: string) {
  return useQuery({
    queryKey: [...ADMIN_LISTS_KEY, programId],
    enabled: !!programId,
    queryFn: async () => {
      const [t, c, f] = await Promise.all([
        supabase.from("program_business_types").select("*").eq("program_id", programId!).order("sort_order"),
        supabase.from("program_communities").select("*").eq("program_id", programId!).order("sort_order"),
        supabase.from("program_focus_areas").select("*").eq("program_id", programId!).order("sort_order"),
      ]);
      if (t.error) throw t.error;
      if (c.error) throw c.error;
      if (f.error) throw f.error;
      return { types: t.data as ListRow[], communities: c.data as ListRow[], focusAreas: f.data as ListRow[] };
    },
  });
}

export interface AdminAppRow {
  id: string;
  user_id: string;
  status: string;
  business_name: string;
  parish: string;
  business_type_id: string | null;
  community_id: string | null;
  hurricane_impact_level: string;
  hurricane_impact_description: string | null;
  focus_area_ids: string[];
  why_join: string | null;
  admin_notes: string | null;
  created_at: string;
  applicant_updated_at: string | null;
  moved_to_community_at: string | null;
  status_email_sent_for: string | null;
  status_email_sent_at: string | null;
  reviewed_at: string | null;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp_number: string | null;
  type_name: string | null;
  community_name: string | null;
  photo_count: number;
  total_score: number;
}

export function useTopShopApplications(programId?: string) {
  return useQuery({
    queryKey: [...ADMIN_APPS_KEY, programId],
    enabled: !!programId,
    queryFn: async (): Promise<AdminAppRow[]> => {
      const { data: apps, error } = await supabase
        .from("program_applications")
        .select("*, business_type:program_business_types(name), community:program_communities(name)")
        .eq("program_id", programId!);
      if (error) throw error;
      const ids = (apps ?? []).map((a) => a.id);
      const userIds = (apps ?? []).map((a) => a.user_id as string);
      if (!ids.length) return [];

      const [profiles, priv, photos, totals] = await Promise.all([
        supabase.from("profiles").select("id, full_name").in("id", userIds),
        supabase.from("profile_private").select("user_id, email, phone").in("user_id", userIds),
        supabase.from("program_application_photos").select("application_id").in("application_id", ids),
        supabase.from("program_application_totals").select("application_id, total_score").in("application_id", ids),
      ]);
      const nameMap = new Map((profiles.data ?? []).map((p) => [p.id, p.full_name]));
      const privMap = new Map((priv.data ?? []).map((p: any) => [p.user_id, p]));
      const photoCount = new Map<string, number>();
      (photos.data ?? []).forEach((p) => photoCount.set(p.application_id, (photoCount.get(p.application_id) ?? 0) + 1));
      const totalMap = new Map((totals.data ?? []).map((t: any) => [t.application_id, Number(t.total_score ?? 0)]));

      return (apps ?? []).map((a: any) => ({
        ...a,
        focus_area_ids: a.focus_area_ids ?? [],
        full_name: nameMap.get(a.user_id) ?? null,
        email: privMap.get(a.user_id)?.email ?? null,
        phone: privMap.get(a.user_id)?.phone ?? null,
        type_name: a.business_type?.name ?? null,
        community_name: a.community?.name ?? null,
        photo_count: photoCount.get(a.id) ?? 0,
        total_score: totalMap.get(a.id) ?? 0,
      }));
    },
  });
}
